/**
 * Layout utility functions for the module layout organizer
 */

/**
 * Deep clone page rows for local state manipulation
 * @param {Array} pageRows - The page rows to clone
 * @returns {Array} - Cloned page rows
 */
export const clonePageRows = (pageRows) => {
  if (!pageRows) return [];
  return pageRows.map((row) => ({
    ...row,
    pageModules: row.pageModules ? [...row.pageModules.map((m) => ({ ...m }))] : [],
  }));
};

/**
 * Check if a row can accept another module
 * @param {Object} row - The target row
 * @param {number} draggedModuleId - ID of module being dragged
 * @returns {boolean} - True if row can accept the module
 */
export const canRowAcceptModule = (row, draggedModuleId) => {
  const currentCount = row.pageModules?.length || 0;
  const isInThisRow = row.pageModules?.some((m) => m.id === draggedModuleId);
  // If module is already in this row, moving within doesn't change count
  return isInThisRow ? currentCount <= 3 : currentCount < 3;
};

/**
 * Find module and its row index in the layout
 * @param {Array} rows - Page rows
 * @param {number} moduleId - Module ID to find
 * @returns {Object|null} - { module, rowIndex, moduleIndex } or null
 */
export const findModuleInLayout = (rows, moduleId) => {
  for (let rowIndex = 0; rowIndex < rows.length; rowIndex++) {
    const row = rows[rowIndex];
    const moduleIndex = row.pageModules?.findIndex((m) => m.id === moduleId);
    if (moduleIndex !== -1 && moduleIndex !== undefined) {
      return {
        module: row.pageModules[moduleIndex],
        rowIndex,
        moduleIndex,
      };
    }
  }
  return null;
};

/**
 * Move module within local state (horizontal move within or between rows)
 * @param {Array} rows - Current rows
 * @param {number} moduleId - Module to move
 * @param {number} targetRowIndex - Target row index
 * @param {string} targetPosition - 'left', 'center', or 'right'
 * @returns {Array} - New rows array
 */
export const moveModuleHorizontal = (rows, moduleId, targetRowIndex, targetPosition) => {
  const newRows = clonePageRows(rows);
  const found = findModuleInLayout(newRows, moduleId);

  if (!found) return rows;

  const { module, rowIndex: sourceRowIndex, moduleIndex: sourceModuleIndex } = found;

  // Remove from source
  newRows[sourceRowIndex].pageModules.splice(sourceModuleIndex, 1);

  // Calculate insert index
  const targetRow = newRows[targetRowIndex];
  let insertIndex;
  if (targetPosition === 'left') {
    insertIndex = 0;
  } else if (targetPosition === 'right') {
    insertIndex = targetRow.pageModules.length;
  } else {
    // center
    insertIndex = 1;
  }

  // Insert at target
  targetRow.pageModules.splice(insertIndex, 0, module);

  // Remove empty rows
  return newRows.filter((row) => row.pageModules.length > 0);
};

/**
 * Move module to a new row (vertical move)
 * @param {Array} rows - Current rows
 * @param {number} moduleId - Module to move
 * @param {number} targetRowPosition - Position for new row (0-based)
 * @returns {Array} - New rows array
 */
export const moveModuleVertical = (rows, moduleId, targetRowPosition) => {
  const newRows = clonePageRows(rows);
  const found = findModuleInLayout(newRows, moduleId);

  if (!found) return rows;

  const { module, rowIndex: sourceRowIndex, moduleIndex: sourceModuleIndex } = found;

  // Remove from source
  newRows[sourceRowIndex].pageModules.splice(sourceModuleIndex, 1);

  // Create new row with the module
  const newRow = {
    id: `temp-${Date.now()}`, // Temporary ID, will be replaced by API
    pageModules: [module],
  };

  // Insert new row at position
  newRows.splice(targetRowPosition, 0, newRow);

  // Remove empty rows
  return newRows.filter((row) => row.pageModules.length > 0);
};

/**
 * Calculate the changes needed to transform from original to new layout
 * @param {Array} originalRows - Original page rows
 * @param {Array} newRows - New page rows after changes
 * @returns {Array} - Array of change objects
 */
export const calculateLayoutChanges = (originalRows, newRows) => {
  const changes = [];

  // Build a map of original positions
  const originalPositions = new Map();
  originalRows.forEach((row, rowIndex) => {
    if (row.pageModules) {
      row.pageModules.forEach((module, moduleIndex) => {
        originalPositions.set(module.id, {
          rowId: row.id,
          rowIndex,
          sortOrder: module.sortOrder || moduleIndex + 1,
        });
      });
    }
  });

  // Compare with new positions
  newRows.forEach((row, rowIndex) => {
    if (row.pageModules) {
      row.pageModules.forEach((module, moduleIndex) => {
        const original = originalPositions.get(module.id);
        if (!original) return;

        const newSortOrder = moduleIndex + 1;
        const rowChanged = original.rowIndex !== rowIndex;
        const sortOrderChanged = original.sortOrder !== newSortOrder;

        if (rowChanged) {
          // Module moved to a different row position
          changes.push({
            type: 'moveToNewRow',
            moduleId: module.id,
            pageRowPosition: rowIndex + 1, // 1-based for API
          });
        } else if (sortOrderChanged) {
          // Module stayed in same row but sort order changed
          changes.push({
            type: 'updateSortOrder',
            moduleId: module.id,
            pageRowId: row.id,
            sortOrder: newSortOrder,
          });
        }
      });
    }
  });

  return changes;
};

/**
 * Get row index from row ID
 * @param {Array} rows - Page rows
 * @param {number|string} rowId - Row ID
 * @returns {number} - Row index or -1
 */
export const getRowIndexById = (rows, rowId) => {
  return rows.findIndex((row) => row.id === rowId);
};
